import React, { useMemo, useState } from 'react'
import { Alert, Col, Form, Row } from 'react-bootstrap'
import classNames from 'classnames'

import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { Button } from '../../../components/Button/Button'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import {
  useDeleteUserShareSettingsMutation,
  useGetUserShareSettingsQuery,
  useSaveUserByIdMutation,
  useSaveUserShareSettingsMutation,
} from '../../../store/user/user'

import { type UserSettings } from '../../../models/User'

import { AccountUserMultiSelector } from './AccountUserMultiSelector'

export type SharingType = 'session' | 'contact' | 'document' | 'scheduling'

const SETTING_KEY: Record<SharingType, 'sessionSharingEnabled' | 'contactSharingEnabled' | 'documentSharingEnabled' | 'schedulingSharingEnabled'> = {
  session: 'sessionSharingEnabled',
  contact: 'contactSharingEnabled',
  document: 'documentSharingEnabled',
  scheduling: 'schedulingSharingEnabled',
}

const SHARE_TYPE: Record<SharingType, 'meeting' | 'contact' | 'document' | 'scheduling'> = {
  session: 'meeting',
  contact: 'contact',
  document: 'document',
  scheduling: 'scheduling',
}

const hasSameUsers = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false
  }
  const set = new Set(a)
  return b.every((id) => set.has(id))
}

interface SharingControlProps {
  userEffectiveSettings: UserSettings
  type: SharingType
  title: string
  toggleTitle: string
  userShareTitle: string
  userShareDescription: string
  informationBoxText?: string
  selectorEmptyText: string
  hideSelector: boolean
  isDisabled: boolean
  allowedUserIds?: string[]
  helperText?: string
}

export const SharingControl: React.FC<SharingControlProps> = ({
  userEffectiveSettings,
  type,
  title,
  toggleTitle,
  userShareTitle,
  userShareDescription,
  informationBoxText,
  selectorEmptyText,
  hideSelector,
  isDisabled,
  allowedUserIds,
  helperText,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const dispatch = useAppDispatch()

  const shareType = SHARE_TYPE[type]
  const settingKey = SETTING_KEY[type]

  const { data: userShareSettings, isFetching: isShareSettingsFetching } = useGetUserShareSettingsQuery({
    accountId,
    userId: currentUserId,
    shareType,
    pageRequest: { page: 0, size: 500 },
  })

  const [saveUserById, { isLoading: isToggleSaving }] = useSaveUserByIdMutation()
  const [deleteUserShareSettings] = useDeleteUserShareSettingsMutation()
  const [saveUserShareSettings, { isLoading: isSelectionSaving }] = useSaveUserShareSettingsMutation()

  const apiSelectedIds = useMemo(() => (userShareSettings?.data ?? []).map((s) => s.sharedWithUserId), [userShareSettings])

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(apiSelectedIds)
  const [lastSyncedIds, setLastSyncedIds] = useState<string[]>(apiSelectedIds)
  const [toggleOverride, setToggleOverride] = useState<boolean | null>(null)

  if (lastSyncedIds !== apiSelectedIds) {
    setLastSyncedIds(apiSelectedIds)
    setSelectedUserIds(apiSelectedIds)
  }

  const effectiveValue = userEffectiveSettings[settingKey] ?? false

  if (toggleOverride !== null && toggleOverride === effectiveValue) {
    setToggleOverride(null)
  }

  const toggleChecked = toggleOverride ?? effectiveValue
  const hasUnsavedChanges = !hasSameUsers(selectedUserIds, apiSelectedIds)

  const isToggleDisabled = isToggleSaving || isSelectionSaving || isDisabled
  const isSelectionChangeDisabled = isToggleDisabled || isShareSettingsFetching || !hasUnsavedChanges

  const handleToggleChange = async (checked: boolean) => {
    const pendingSave = saveUserById({
      accountId,
      userId: currentUserId,
      userData: { settings: { [settingKey]: checked } },
    })

    setToggleOverride(checked)

    try {
      await pendingSave.unwrap()
    } catch {
      setToggleOverride(!checked)
      return
    }

    if (!checked) {
      deleteUserShareSettings({ accountId, userId: currentUserId, shareType })
    }
  }

  const onCancel = () => {
    setSelectedUserIds(apiSelectedIds)
  }

  const onSaveChanges = async () => {
    await saveUserShareSettings({
      accountId,
      userId: currentUserId,
      payload: { shareType, sharedWithUserIds: selectedUserIds },
    }).unwrap()
    dispatch(addToast({ variant: 'success', title: 'Sharing settings saved', messages: ['Sharing settings saved successfully.'] }))
  }

  return (
    <>
      <Row>
        <Col>
          <h4>{title}</h4>
        </Col>
      </Row>
      {helperText ? (
        <Row>
          <Col className='paragraph-medium zocks-text-gray-600'>{helperText}</Col>
        </Row>
      ) : null}
      <Row className='gap-3 p-0 align-items-center'>
        <Col>
          {informationBoxText && (
            <Alert variant='warning' className='d-flex gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>{informationBoxText}</span>
            </Alert>
          )}
          <ScopedBlock
            scope={{
              scope: 'user:update',
              superScope: ['user:admin'],
              resource: `user:${currentUserId}`,
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Row className='align-items-center'>
              <Col xs='auto'>
                <Form.Switch disabled={isToggleDisabled} checked={toggleChecked} onChange={(e) => handleToggleChange(e.target.checked)} />
              </Col>
              <Col className='paragraph-medium'>{toggleTitle}</Col>
            </Row>
          </ScopedBlock>
        </Col>
        {toggleChecked && !hideSelector && (
          <>
            <h5>{userShareTitle}</h5>
            <div className='paragraph-medium'>{userShareDescription}</div>
            <AccountUserMultiSelector
              isCreatable={false}
              excludeUserId={currentUserId}
              selectedUserIds={selectedUserIds}
              onSelectedUsersChange={setSelectedUserIds}
              noSelectedUserText={selectorEmptyText}
              allowedUserIds={allowedUserIds}
            />
            <div className='d-flex zocks-gap-0.625'>
              <Button variant='primary' onClick={() => onSaveChanges()} loading={isSelectionSaving} disabled={isSelectionChangeDisabled}>
                Save changes
              </Button>
              <Button
                variant='borderless'
                className={classNames({ 'zocks-text-primary': !isSelectionChangeDisabled })}
                onClick={() => onCancel()}
                disabled={isSelectionChangeDisabled}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </Row>
    </>
  )
}
